import type { FlightLeg } from "./types";
import { DEFAULT_AIRCRAFT_ID } from "./types";
import { flightLegFingerprint } from "./flightDedupe";
import { legInClosedDateRange, routeSnippet } from "./pilotCostInvoiceLegPicker";
import {
  createPilotCostInvoiceWithLegs,
  deletePilotCostInvoice,
  listPilotCostInvoices,
  type PilotCostInvoiceRow,
} from "../supabase/pilotCostInvoices";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function isUuid(v: string | undefined): boolean {
  if (!v) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    v.trim()
  );
}

function fmtMoney(n: number): string {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(x);
}

function formatInvoiceSavedRow(r: PilotCostInvoiceRow): string {
  const legs = r.pilot_cost_invoice_legs ?? [];
  const n = legs.length;
  const period = `${r.period_start} → ${r.period_end}`;
  return `<tr>
    <td>${esc(r.title || "(untitled)")}</td>
    <td class="tabular-nums">${fmtMoney(Number(r.amount_usd))}</td>
    <td>${esc(period)}</td>
    <td>${n}</td>
    <td><button type="button" class="secondary" data-pilot-inv-delete="${esc(r.id)}">Delete</button></td>
  </tr>`;
}

export async function refreshPilotCostSavedInvoices(app: HTMLElement): Promise<void> {
  const tbody = app.querySelector("#pilot-inv-saved-tbody");
  const msg = app.querySelector<HTMLParagraphElement>("#pilot-inv-msg");
  if (!tbody) return;

  const { data, error } = await listPilotCostInvoices();
  if (error) {
    if (msg) {
      msg.textContent = error;
      msg.classList.add("error");
    }
    tbody.innerHTML = "";
    return;
  }
  if (msg) {
    msg.classList.remove("error");
    msg.textContent =
      data.length === 0
        ? "No saved pilot invoices yet — use the form above to add one."
        : `${data.length} saved invoice record(s).`;
  }
  tbody.innerHTML = data.map(formatInvoiceSavedRow).join("");
}

export function wirePilotCostInvoicePanel(
  app: HTMLElement,
  opts: { showCloud: boolean; currentLegs: () => FlightLeg[] }
): void {
  const { showCloud, currentLegs } = opts;

  app.querySelector("#pilot-inv-load-legs")?.addEventListener("click", () => {
    const msg = app.querySelector<HTMLParagraphElement>("#pilot-inv-msg");
    const wrap = app.querySelector<HTMLDivElement>("#pilot-inv-leg-list");
    if (!wrap) return;
    const from = app.querySelector<HTMLInputElement>("#pilot-inv-period-start")?.value?.trim() ?? "";
    const to = app.querySelector<HTMLInputElement>("#pilot-inv-period-end")?.value?.trim() ?? "";
    if (!from || !to) {
      if (msg) {
        msg.textContent = "Set period start and end, then load legs.";
        msg.classList.add("error");
      }
      wrap.innerHTML = "";
      return;
    }
    if (from > to) {
      if (msg) {
        msg.textContent = "Period start must be on or before period end.";
        msg.classList.add("error");
      }
      wrap.innerHTML = "";
      return;
    }
    const legs = currentLegs().filter((l) => legInClosedDateRange(l.flightDate, from, to));
    legs.sort((a, b) => {
      const c = a.flightDate.localeCompare(b.flightDate);
      if (c !== 0) return c;
      const f = a.fromIcao.localeCompare(b.fromIcao);
      if (f !== 0) return f;
      return a.toIcao.localeCompare(b.toIcao);
    });
    if (msg) {
      msg.classList.remove("error");
      msg.textContent =
        legs.length === 0
          ? "No worksheet legs in that date range (check Bulk / cloud / Manual rows)."
          : `${legs.length} leg(s) in range — tick the ones this invoice covers.`;
    }
    if (legs.length === 0) {
      wrap.innerHTML = "";
      return;
    }
    wrap.innerHTML = legs
      .map((leg) => {
        const fp = encodeURIComponent(flightLegFingerprint(leg));
        const snip = esc(routeSnippet(leg.routeNotes, 72));
        const idNote = isUuid(leg.id) ? "" : ' <span class="muted-small">(no cloud id)</span>';
        return `<label class="pilot-inv-leg-row">
  <input type="checkbox" name="pilot-inv-leg" value="${fp}" checked />
  <span><strong>${esc(leg.flightDate.slice(0, 10))}</strong> · ${esc(leg.fromIcao)} → ${esc(leg.toIcao)}${idNote}${
    snip ? `<span class="muted-small pilot-inv-route"> — ${snip}</span>` : ""
  }</span>
</label>`;
      })
      .join("");
  });

  app.querySelector("#pilot-inv-select-all")?.addEventListener("click", () => {
    for (const el of app.querySelectorAll<HTMLInputElement>('input[name="pilot-inv-leg"]')) {
      el.checked = true;
    }
  });

  app.querySelector("#pilot-inv-select-none")?.addEventListener("click", () => {
    for (const el of app.querySelectorAll<HTMLInputElement>('input[name="pilot-inv-leg"]')) {
      el.checked = false;
    }
  });

  app.querySelector("#pilot-inv-save")?.addEventListener("click", async () => {
    const msg = app.querySelector<HTMLParagraphElement>("#pilot-inv-msg");
    if (!showCloud) {
      if (msg) {
        msg.textContent = "Configure Supabase (Data tab) to save pilot invoices to the cloud.";
        msg.classList.add("error");
      }
      return;
    }
    const title = app.querySelector<HTMLInputElement>("#pilot-inv-title")?.value?.trim() ?? "";
    const amountRaw = app.querySelector<HTMLInputElement>("#pilot-inv-amount")?.value?.trim() ?? "";
    const from = app.querySelector<HTMLInputElement>("#pilot-inv-period-start")?.value?.trim() ?? "";
    const to = app.querySelector<HTMLInputElement>("#pilot-inv-period-end")?.value?.trim() ?? "";
    const vendorNote = app.querySelector<HTMLTextAreaElement>("#pilot-inv-vendor-note")?.value ?? "";

    const amount = Number(amountRaw);
    if (!title) {
      if (msg) {
        msg.textContent = "Enter a short title (e.g. vendor + month).";
        msg.classList.add("error");
      }
      return;
    }
    if (!Number.isFinite(amount) || amount < 0) {
      if (msg) {
        msg.textContent = "Enter a valid invoiced amount (USD).";
        msg.classList.add("error");
      }
      return;
    }
    if (!from || !to || from > to) {
      if (msg) {
        msg.textContent = "Set a valid invoice period (start ≤ end).";
        msg.classList.add("error");
      }
      return;
    }

    const checked = [...app.querySelectorAll<HTMLInputElement>('input[name="pilot-inv-leg"]:checked')];
    if (checked.length === 0) {
      if (msg) {
        msg.textContent = "Load legs for the period, then leave at least one flight checked.";
        msg.classList.add("error");
      }
      return;
    }

    const fpSet = new Set(
      checked.map((el) => {
        try {
          return decodeURIComponent(el.value);
        } catch {
          return el.value;
        }
      })
    );
    const chosen = currentLegs().filter((l) => fpSet.has(flightLegFingerprint(l)));
    if (chosen.length === 0) {
      if (msg) {
        msg.textContent = "Could not match checked rows to the current worksheet — click Load legs again.";
        msg.classList.add("error");
      }
      return;
    }

    const legsPayload = chosen.map((l) => ({
      flight_leg_id: isUuid(l.id) ? (l.id as string) : null,
      leg_flight_date: l.flightDate.trim().slice(0, 10),
      leg_from_icao: l.fromIcao.trim().toUpperCase(),
      leg_to_icao: l.toIcao.trim().toUpperCase(),
      leg_route_snippet: routeSnippet(l.routeNotes, 200),
    }));

    if (msg) {
      msg.textContent = "Saving…";
      msg.classList.remove("error");
    }
    const { error } = await createPilotCostInvoiceWithLegs(
      {
        aircraft_id: DEFAULT_AIRCRAFT_ID,
        title,
        amount_usd: amount,
        period_start: from,
        period_end: to,
        vendor_note: vendorNote.trim(),
      },
      legsPayload
    );
    if (error) {
      if (msg) {
        msg.textContent = error;
        msg.classList.add("error");
      }
      return;
    }
    if (msg) {
      msg.textContent = "Saved pilot invoice and linked legs.";
      msg.classList.remove("error");
    }
    await refreshPilotCostSavedInvoices(app);
  });

  app.querySelector("#pilot-inv-saved-table")?.addEventListener("click", async (e) => {
    const t = (e.target as HTMLElement).closest("[data-pilot-inv-delete]");
    if (!(t instanceof HTMLButtonElement)) return;
    const id = t.getAttribute("data-pilot-inv-delete");
    if (!id) return;
    const msg = app.querySelector<HTMLParagraphElement>("#pilot-inv-msg");
    if (!showCloud) return;
    if (msg) {
      msg.textContent = "Deleting…";
      msg.classList.remove("error");
    }
    const { error } = await deletePilotCostInvoice(id);
    if (error) {
      if (msg) {
        msg.textContent = error;
        msg.classList.add("error");
      }
      return;
    }
    if (msg) {
      msg.textContent = "Deleted.";
      msg.classList.remove("error");
    }
    await refreshPilotCostSavedInvoices(app);
  });
}
